import { useState, type Key } from 'react';
import {
  Table,
  Button,
  Tag,
  Typography,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Popconfirm,
  App,
} from 'antd';
import { PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import { LIST_PAGINATION, LIST_SCROLL_Y } from '../../lib/tablePagination';
import type { UserProfile } from '@bom/shared';
import { PASSWORD_MIN_LENGTH, PRIMARY_ADMIN_EMAIL } from '@bom/shared';
import {
  useUsers,
  useCreateUser,
  useUpdateUser,
  useDisableUser,
  useDeleteUser,
  useResetPassword,
} from '../../api/users';
import { useSessionStore } from '../../stores/sessionStore';
import { fmtTime } from '../../lib/format';
import { ApiError } from '../../lib/apiClient';
import { ExcelImportButton } from '../../components/ExcelImportButton';
import {
  parseUserSheet,
  exportRowsToXlsx,
  USER_TEMPLATE_HEADERS,
  USER_TEMPLATE_SAMPLE,
  type UserImportRow,
} from '../../lib/excel';

const isPrimaryAdmin = (email?: string): boolean =>
  (email ?? '').toLowerCase() === PRIMARY_ADMIN_EMAIL;

export function UsersPage() {
  const { message, modal } = App.useApp();
  const myId = useSessionStore((s) => s.user?.id);
  const users = useUsers();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const disableUser = useDisableUser();
  const deleteUser = useDeleteUser();
  const resetPw = useResetPassword();

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [pwForm] = Form.useForm();
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserProfile | null>(null);
  const [pwUser, setPwUser] = useState<UserProfile | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [delDone, setDelDone] = useState(0);

  const onBulkDelete = async () => {
    setBulkDeleting(true);
    setDelDone(0);
    let done = 0;
    const results = await Promise.allSettled(
      selectedKeys.map((uid) =>
        deleteUser.mutateAsync(uid).finally(() => {
          done += 1;
          setDelDone(done);
        }),
      ),
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const fail = results.length - ok;
    setBulkDeleting(false);
    setSelectedKeys([]);
    if (fail === 0) message.success(`已删除 ${ok} 个用户`);
    else message.warning(`删除 ${ok} 个，${fail} 个失败`);
  };

  const onCreate = () =>
    createForm.validateFields().then((v) =>
      createUser.mutate(v, {
        onSuccess: () => {
          message.success('用户已创建（须首次登录改密码）');
          setCreateOpen(false);
          createForm.resetFields();
        },
        onError: (e) =>
          message.error(e instanceof ApiError && e.status === 409 ? '邮箱已存在' : '创建失败'),
      }),
    );

  const onEdit = () =>
    editForm.validateFields().then((v) => {
      if (!editUser) return;
      updateUser.mutate(
        { uid: editUser.id, body: v },
        {
          onSuccess: () => {
            message.success('已更新');
            setEditUser(null);
          },
          onError: () => message.error('更新失败'),
        },
      );
    });

  const onResetPw = () =>
    pwForm.validateFields().then((v: { new_password: string }) => {
      if (!pwUser) return;
      resetPw.mutate(
        { uid: pwUser.id, new_password: v.new_password },
        {
          onSuccess: () => {
            message.success('密码已重设');
            setPwUser(null);
            pwForm.resetFields();
          },
          onError: () => message.error('重设失败'),
        },
      );
    });

  const onDisable = (u: UserProfile) =>
    modal.confirm({
      title: `停用用户 ${u.email}？`,
      content: '停用后该用户 1 分钟内 token 失效，无法登录。',
      okButtonProps: { danger: true },
      onOk: () =>
        disableUser.mutateAsync(u.id).then(
          () => message.success('已停用'),
          () => message.error('停用失败'),
        ),
    });

  const onDelete = (u: UserProfile) =>
    modal.confirm({
      title: `删除用户 ${u.email}？`,
      content: '将永久删除该账号及其所有产品成员关系、通知、操作日志，且邮箱可再次注册；不可恢复。',
      okText: '删除',
      okButtonProps: { danger: true },
      onOk: () =>
        deleteUser.mutateAsync(u.id).then(
          () => message.success('已删除'),
          (e) =>
            message.error(`删除失败：${e instanceof ApiError ? e.message : (e as Error).message}`),
        ),
    });

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          用户管理
        </Typography.Title>
        <Space>
          {selectedKeys.length > 0 && (
            <Popconfirm
              title={`删除选中的 ${selectedKeys.length} 个用户？`}
              description="将永久删除账号及其成员关系/日志，不可恢复；本人账号不可删。"
              okText="删除"
              okButtonProps={{ danger: true }}
              cancelText="取消"
              onConfirm={onBulkDelete}
            >
              <Button danger loading={bulkDeleting}>
                {bulkDeleting
                  ? `删除中 ${delDone}/${selectedKeys.length}`
                  : `批量删除 (${selectedKeys.length})`}
              </Button>
            </Popconfirm>
          )}
          <Button
            icon={<DownloadOutlined />}
            onClick={() =>
              exportRowsToXlsx(
                `用户列表_${new Date().toISOString().slice(0, 10)}`,
                ['邮箱', '姓名', '平台管理员', '状态', '创建时间'],
                (users.data ?? []).map((u) => [
                  u.email,
                  u.name,
                  u.is_platform_admin ? '是' : '否',
                  u.status === 'active' ? '启用' : u.status === 'locked' ? '锁定' : '停用',
                  u.created_at ? new Date(u.created_at).toLocaleString('zh-CN') : '',
                ]),
              )
            }
          >
            导出
          </Button>
          <ExcelImportButton<UserImportRow>
            templateName="用户导入模板"
            templateHeaders={USER_TEMPLATE_HEADERS}
            templateSample={USER_TEMPLATE_SAMPLE}
            parse={parseUserSheet}
            importOne={async (row) => {
              await createUser.mutateAsync(row);
            }}
            onDone={() => users.refetch()}
            label="导入用户"
            previewColumns={[
              { title: '邮箱', dataIndex: 'email' },
              { title: '姓名', dataIndex: 'name' },
              {
                title: '平台管理员',
                dataIndex: 'is_platform_admin',
                editor: {
                  type: 'select',
                  options: [
                    { value: true, label: '是' },
                    { value: false, label: '否' },
                  ],
                },
              },
              { title: '初始密码', dataIndex: 'initial_password' },
            ]}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            新建用户
          </Button>
        </Space>
      </Space>

      <Table<UserProfile>
        rowKey="id"
        loading={users.isLoading}
        dataSource={users.data ?? []}
        scroll={{ y: LIST_SCROLL_Y }}
        pagination={LIST_PAGINATION}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: (keys: Key[]) => setSelectedKeys(keys.map(String)),
          getCheckboxProps: (u) => ({ disabled: u.id === myId || isPrimaryAdmin(u.email) }), // 本人/主账号不可删
        }}
        columns={[
          {
            title: '邮箱',
            dataIndex: 'email',
            render: (v: string) => (
              <Space size="small">
                <span>{v}</span>
                {isPrimaryAdmin(v) && <Tag color="purple">主账号</Tag>}
              </Space>
            ),
          },
          { title: '姓名', dataIndex: 'name', width: 140 },
          {
            title: '平台管理员',
            dataIndex: 'is_platform_admin',
            width: 110,
            render: (v: boolean) => (v ? <Tag color="gold">是</Tag> : <Tag>否</Tag>),
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: 90,
            render: (s: string) => (
              <Tag color={s === 'active' ? 'green' : s === 'locked' ? 'orange' : 'red'}>
                {s === 'active' ? '启用' : s === 'locked' ? '锁定' : '停用'}
              </Tag>
            ),
          },
          {
            title: '改密',
            dataIndex: 'must_change_password',
            width: 80,
            render: (v?: boolean) => (v ? <Tag color="orange">待改</Tag> : '—'),
          },
          { title: '创建时间', dataIndex: 'created_at', width: 170, render: fmtTime },
          {
            title: '操作',
            key: 'action',
            width: 260,
            render: (_: unknown, u: UserProfile) => (
              <Space size="small">
                <Button
                  size="small"
                  type="link"
                  onClick={() => {
                    setEditUser(u);
                    editForm.setFieldsValue(u);
                  }}
                >
                  编辑
                </Button>
                <Button size="small" type="link" onClick={() => setPwUser(u)}>
                  重设密码
                </Button>
                {u.status !== 'disabled' && !isPrimaryAdmin(u.email) && (
                  <Button size="small" type="link" danger onClick={() => onDisable(u)}>
                    停用
                  </Button>
                )}
                {u.id !== myId && !isPrimaryAdmin(u.email) && (
                  <Button size="small" type="link" danger onClick={() => onDelete(u)}>
                    删除
                  </Button>
                )}
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title="新建用户"
        open={createOpen}
        onOk={onCreate}
        confirmLoading={createUser.isPending}
        onCancel={() => setCreateOpen(false)}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" preserve={false}>
          <Form.Item
            name="email"
            label="邮箱"
            rules={[{ required: true, type: 'email', message: '请输入有效邮箱' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="initial_password"
            label="初始密码"
            rules={[
              {
                required: true,
                min: PASSWORD_MIN_LENGTH,
                message: `至少 ${PASSWORD_MIN_LENGTH} 位`,
              },
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="is_platform_admin"
            label="平台管理员"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`编辑用户 ${editUser?.email ?? ''}`}
        open={!!editUser}
        onOk={onEdit}
        confirmLoading={updateUser.isPending}
        onCancel={() => setEditUser(null)}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" preserve={false}>
          <Form.Item name="name" label="姓名">
            <Input />
          </Form.Item>
          <Form.Item
            name="status"
            label="状态"
            extra={isPrimaryAdmin(editUser?.email) ? '主账号不可停用/锁定' : undefined}
          >
            <Select
              disabled={isPrimaryAdmin(editUser?.email)}
              options={[
                { value: 'active', label: '启用' },
                { value: 'disabled', label: '停用' },
                { value: 'locked', label: '锁定' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`重设密码 ${pwUser?.email ?? ''}`}
        open={!!pwUser}
        onOk={onResetPw}
        confirmLoading={resetPw.isPending}
        onCancel={() => setPwUser(null)}
        destroyOnClose
      >
        <Form form={pwForm} layout="vertical" preserve={false}>
          <Form.Item
            name="new_password"
            label="新密码"
            rules={[
              {
                required: true,
                min: PASSWORD_MIN_LENGTH,
                message: `至少 ${PASSWORD_MIN_LENGTH} 位`,
              },
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
